<template>
    <ClientOnly fallbackTag="div">
        <template #fallback>
            <div
                v-for="(item, idx) in bannerItems"
                :key="idx"
                class="banner-fallback-item"
                :style="{ height: bannerHeight }"
            >
                <BannerCard :item="item" :idx="idx" />
            </div>
        </template>

        <div
            v-if="isScrollList"
            class="banner-scroll-list"
            :style="{ height: bannerHeight }"
        >
            <button
                class="banner-scroll-nav banner-scroll-nav--prev"
                type="button"
                :aria-label="ct('上一张')"
                :disabled="!canScrollPrev"
                @click="scrollPrev"
            >
                <svg
                    class="banner-scroll-nav-icon"
                    viewBox="0 0 16 16"
                    aria-hidden="true"
                >
                    <path
                        d="M9.5 3.5 5 8l4.5 4.5"
                        fill="none"
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="1.8"
                    />
                </svg>
            </button>

            <div ref="scrollViewportRef" class="banner-scroll-viewport">
                <div
                    class="banner-scroll-track"
                    :style="{
                        transform: `translateX(-${scrollOffset}px)`,
                        gap: `${scrollGap}px`,
                    }"
                >
                    <div
                        v-for="(item, idx) in bannerItems"
                        :key="idx"
                        class="banner-scroll-item"
                        :style="getScrollItemStyle(item)"
                    >
                        <BannerCard :item="item" :idx="idx" />
                    </div>
                </div>
            </div>

            <button
                class="banner-scroll-nav banner-scroll-nav--next"
                type="button"
                :aria-label="ct('下一张')"
                :disabled="!canScrollNext"
                @click="scrollNext"
            >
                <svg
                    class="banner-scroll-nav-icon"
                    viewBox="0 0 16 16"
                    aria-hidden="true"
                >
                    <path
                        d="M6.5 3.5 11 8l-4.5 4.5"
                        fill="none"
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="1.8"
                    />
                </svg>
            </button>
        </div>

        <el-carousel
            v-else
            class="banner-item-wrap"
            :height="bannerHeight"
            :initial-index="carouselInitialIndex"
            :trigger="carouselTrigger"
            :autoplay="carouselAutoplay"
            :interval="carouselInterval"
            :indicator-position="carouselIndicatorPosition"
            :arrow="carouselArrow"
            :type="carouselType"
            :card-scale="carouselCardScale"
            :loop="carouselLoop"
            :direction="carouselDirection"
            :pause-on-hover="carouselPauseOnHover"
            :motion-blur="carouselMotionBlur"
        >
            <el-carousel-item
                v-for="(item, idx) in bannerItems"
                :key="idx"
                class="banner-carousel-item"
            >
                <BannerCard :item="item" :idx="idx" />
            </el-carousel-item>
        </el-carousel>
    </ClientOnly>
</template>

<script setup>
const ct = useCommonUiTranslation()

import {
    computed,
    defineComponent,
    h,
    inject,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
} from "vue";
import LazyImage from "@/components/common/LazyImage/index.vue";
import { getSafeLink, isHttpLink, isTarget_Blank } from "@/utils/globalData.js";
import { localizeAssetFields } from "@/utils/lowCodeLocalizedAsset.js";
import { useLowCodeActionRuntime } from "@/composables/useLowCodeActionRuntime.js";
import { isLowCodePopupPageAction } from "@/utils/lowCodePopupPage.js";
import { trackLowCodeEvent } from "@/utils/lowCodeTracking.js";

const props = defineProps({
    currentElementIndex: {
        type: Array,
        default: () => [],
    },
    idx: {
        type: Number,
        default: -1,
    },
    parentKeys: {
        type: Array,
        default: () => [],
    },
    disabled: {
        type: Boolean,
        default: true,
    },
    config: {
        type: Object,
        default: () => ({}),
    },
});
const { dispatchLowCodeAction } = useLowCodeActionRuntime();
const getConfigJson = inject("getConfigJson", () => ({}));

const resolveCssSize = (value) => {
    if (typeof value === "number" && value > 0) {
        return `${value}px`;
    }
    if (typeof value === "string" && value.trim()) {
        return value;
    }
    return undefined;
};

const toNumberSize = (value, fallback) => {
    if (typeof value === "number" && value > 0) return value;
    if (typeof value === "string") {
        const parsed = Number.parseFloat(value);
        if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
    return fallback;
};

const normalizeLegacyInterval = (timer) => {
    if (typeof timer !== "number" || timer <= 0) {
        return 3000;
    }
    return timer < 1000 ? timer * 1000 : timer;
};

const { locale } = useI18n();
const bannerItems = computed(() => {
    const items = Array.isArray(props.config?.imageConfigs) ? props.config.imageConfigs : [];
    return items.map((item) => localizeAssetFields(item, ["url"], locale.value));
});

const bannerHeight = computed(() => {
    return resolveCssSize(props.config?.height) || "150px";
});

const isScrollList = computed(() => props.config?.type === "scrolllist");

const carouselInitialIndex = computed(() => {
    return typeof props.config?.initialIndex === "number" && props.config.initialIndex >= 0
        ? props.config.initialIndex
        : 0;
});

const carouselTrigger = computed(() => {
    return props.config?.trigger === "click" ? "click" : "hover";
});

const carouselAutoplay = computed(() => {
    if (typeof props.config?.isAuto === "boolean") {
        return props.config.isAuto;
    }
    if (typeof props.config?.autoplay === "boolean") {
        return props.config.autoplay;
    }
    return true;
});

const carouselInterval = computed(() => {
    if (typeof props.config?.interval === "number" && props.config.interval > 0) {
        return props.config.interval;
    }
    return normalizeLegacyInterval(props.config?.timer);
});

const carouselIndicatorPosition = computed(() => {
    const value = props.config?.indicatorPosition;
    return value === "outside" || value === "none" ? value : "";
});

const carouselArrow = computed(() => {
    const value = props.config?.arrow;
    return value === "always" || value === "never" ? value : "hover";
});

const carouselType = computed(() => {
    return props.config?.type === "card" ? "card" : "";
});

const carouselCardScale = computed(() => {
    return typeof props.config?.cardScale === "number" ? props.config.cardScale : 0.83;
});

const carouselLoop = computed(() => {
    return typeof props.config?.loop === "boolean" ? props.config.loop : true;
});

const carouselDirection = computed(() => {
    return props.config?.direction === "vertical" ? "vertical" : "horizontal";
});

const carouselPauseOnHover = computed(() => {
    return typeof props.config?.pauseOnHover === "boolean"
        ? props.config.pauseOnHover
        : true;
});

const carouselMotionBlur = computed(() => {
    return !!props.config?.motionBlur;
});

const defaultScrollItemWidth = computed(() => {
    const firstSizedItem = bannerItems.value.find((item) => item?.width);
    return toNumberSize(firstSizedItem?.width, 340);
});

const defaultScrollItemHeight = computed(() => {
    const firstSizedItem = bannerItems.value.find((item) => item?.height);
    return toNumberSize(firstSizedItem?.height, toNumberSize(props.config?.height, 160));
});

const scrollGap = computed(() => 24);
const scrollViewportRef = ref(null);
const viewportWidth = ref(0);
const scrollIndex = ref(0);

const getScrollItemWidth = (item) => toNumberSize(item?.width, defaultScrollItemWidth.value);
const getScrollItemHeight = (item) => toNumberSize(item?.height, defaultScrollItemHeight.value);

const getItemRadiusStyle = (item) => {
    const radius = resolveCssSize(item?.radius);
    return radius ? { borderRadius: radius, overflow: "hidden" } : {};
};

const getScrollItemStyle = (item) => {
    return {
        width: `${getScrollItemWidth(item)}px`,
        height: `${getScrollItemHeight(item)}px`,
        ...getItemRadiusStyle(item),
    };
};

const getFillCardStyle = (item) => {
    return getItemRadiusStyle(item);
};

const getMediaStyle = (item, mode = "fill") => {
    const style = {};
    if (mode === "custom") {
        const width = resolveCssSize(item?.width);
        const height = resolveCssSize(item?.height);
        if (width) style.width = width;
        if (height) style.height = height;
    }
    return style;
};

const getBackgroundStyle = (item, mode = "fill") => {
    return {
        ...getMediaStyle(item, mode),
        ...(item?.url ? { backgroundImage: `url(${item.url})` } : {}),
    };
};

const createBannerCardComponent = () =>
    defineComponent({
        name: "BannerCard",
        props: {
            item: {
                type: Object,
                required: true,
            },
            idx: {
                type: Number,
                required: true,
            },
        },
        setup(cardProps) {
            const clickAction = computed(
                () => {
                    const candidates = [
                        cardProps.item?.clickConfig,
                        cardProps.item?.clickAction,
                        cardProps.item?.link,
                    ];
                    return candidates.find((item) => isLowCodePopupPageAction(item)) ||
                        cardProps.item?.clickConfig ||
                        cardProps.item?.clickAction ||
                        cardProps.item?.link;
                }
            );
            const useCustomSize = computed(() => {
                return isScrollList.value || !!resolveCssSize(cardProps.item?.width) || !!resolveCssSize(cardProps.item?.height);
            });

            const cardWrapClass = computed(() => {
                return [
                    "banner-card",
                    useCustomSize.value ? "banner-card--custom" : "banner-card--fill",
                ];
            });

            const cardWrapStyle = computed(() => {
                return useCustomSize.value
                    ? getScrollItemStyle(cardProps.item)
                    : getFillCardStyle(cardProps.item);
            });

            const mediaClass = computed(() => {
                return [
                    "banner-media",
                    useCustomSize.value ? "banner-media--custom" : "banner-media--fill",
                    cardProps.item?.isBackground ? "banner-bg" : "",
                ];
            });

            return () =>
                h(
                    "a",
                    {
                        href: isLowCodePopupPageAction(clickAction.value)
                            ? undefined
                            : getSafeLink(cardProps.item.link) || undefined,
                        target:
                            !isLowCodePopupPageAction(clickAction.value) &&
                            isHttpLink(cardProps.item.link)
                                ? isTarget_Blank()
                                : undefined,
                        class: [
                            "banner-link",
                            isLowCodePopupPageAction(clickAction.value)
                                ? "banner-link--interactive"
                                : "",
                        ],
                        onClick: (event) => {
                            if (props.disabled) {
                                const page = typeof getConfigJson === "function" ? getConfigJson() : {};
                                trackLowCodeEvent(cardProps.item?.trackingConfig, {
                                    trigger: "click",
                                    element: cardProps.item,
                                    elementType: "banner_item",
                                    elementIndex: [...props.parentKeys, props.idx, cardProps.idx],
                                    page,
                                    binding: page?.bindData || {},
                                    locale: locale.value,
                                });
                            }
                            if (!isLowCodePopupPageAction(clickAction.value)) return;
                            event?.preventDefault?.();
                            event?.stopPropagation?.();
                            dispatchLowCodeAction(
                                clickAction.value,
                                typeof getConfigJson === "function" ? getConfigJson() : {}
                            );
                        },
                    },
                    [
                        h(
                            "div",
                            {
                                class: cardWrapClass.value,
                                style: cardWrapStyle.value,
                            },
                            cardProps.item.isBackground
                                ? [
                                      h("div", {
                                          class: mediaClass.value,
                                          style: getBackgroundStyle(
                                              cardProps.item,
                                              useCustomSize.value ? "custom" : "fill"
                                          ),
                                      }),
                                      cardProps.item.slot
                                          ? h("div", {
                                                class: `banner-slot-bg banner-slot-${cardProps.idx}`,
                                                innerHTML: cardProps.item.slot,
                                            })
                                          : null,
                                  ]
                                : [
                                      h(LazyImage, {
                                          class: mediaClass.value,
                                          style: getMediaStyle(
                                              cardProps.item,
                                              useCustomSize.value ? "custom" : "fill"
                                          ),
                                          src: cardProps.item.url,
                                          fit: "fill",
                                      }),
                                      cardProps.item.slot
                                          ? h("div", {
                                                class: `banner-slot banner-slot-${cardProps.idx}`,
                                                innerHTML: cardProps.item.slot,
                                            })
                                          : null,
                                  ]
                        ),
                    ]
                );
        },
    });

const BannerCard = createBannerCardComponent();

const totalTrackWidth = computed(() => {
    return bannerItems.value.reduce((sum, item, index) => {
        const itemWidth = getScrollItemWidth(item);
        return sum + itemWidth + (index > 0 ? scrollGap.value : 0);
    }, 0);
});

const visibleCount = computed(() => {
    if (!viewportWidth.value) return 1;
    const baseWidth = defaultScrollItemWidth.value + scrollGap.value;
    return Math.max(1, Math.floor((viewportWidth.value + scrollGap.value) / baseWidth));
});

const maxScrollIndex = computed(() => {
    return Math.max(0, bannerItems.value.length - visibleCount.value);
});

const scrollOffset = computed(() => {
    const cappedIndex = Math.min(scrollIndex.value, maxScrollIndex.value);
    let offset = 0;
    for (let i = 0; i < cappedIndex; i += 1) {
        offset += getScrollItemWidth(bannerItems.value[i]) + scrollGap.value;
    }
    const maxOffset = Math.max(0, totalTrackWidth.value - viewportWidth.value);
    return Math.min(offset, maxOffset);
});

const canScrollPrev = computed(() => scrollIndex.value > 0);
const canScrollNext = computed(() => scrollIndex.value < maxScrollIndex.value);

const syncViewportWidth = () => {
    viewportWidth.value = scrollViewportRef.value?.clientWidth || 0;
    if (scrollIndex.value > maxScrollIndex.value) {
        scrollIndex.value = maxScrollIndex.value;
    }
};

const scrollPrev = () => {
    if (!canScrollPrev.value) return;
    scrollIndex.value -= 1;
};

const scrollNext = () => {
    if (!canScrollNext.value) return;
    scrollIndex.value += 1;
};

const onResize = () => {
    syncViewportWidth();
};

watch(
    () => bannerItems.value.length,
    () => {
        nextTick(() => {
            syncViewportWidth();
        });
    },
    { immediate: true }
);

watch(isScrollList, () => {
    nextTick(() => {
        scrollIndex.value = 0;
        syncViewportWidth();
    });
});

onMounted(() => {
    nextTick(() => {
        syncViewportWidth();
    });
    window.addEventListener("resize", onResize);
});

onBeforeUnmount(() => {
    window.removeEventListener("resize", onResize);
});
</script>

<style scoped>
.banner-fallback-item {
    width: 100%;
}

.banner-item-wrap {
    width: 100%;
    height: 100%;
}

.banner-carousel-item {
    overflow: hidden;
}

.banner-link {
    display: block;
    position: relative;
    width: 100%;
    height: 100%;
}

.banner-card {
    position: relative;
    width: 100%;
    height: 100%;
}

.banner-card--custom {
    flex: 0 0 auto;
}

.banner-card--fill {
    width: 100%;
    height: 100%;
}

.banner-media {
    width: 100%;
    height: 100%;
}

.banner-media--custom {
    width: 100%;
    height: 100%;
}

.banner-media--fill {
    width: 100%;
    height: 100%;
}

.banner-bg {
    background-position: center center;
    background-repeat: no-repeat;
    background-size: cover;
}

.banner-slot,
.banner-slot-bg {
    position: absolute;
    left: 0;
    top: 50px;
    z-index: 2;
}

.banner-media :deep(.lazy-image) {
    width: 100%;
    height: 100%;
}

.banner-media :deep(.lazy-image__img) {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.banner-scroll-list {
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
}

.banner-scroll-viewport {
    flex: 1;
    overflow: hidden;
}

.banner-scroll-track {
    display: flex;
    align-items: center;
    transition: transform 0.35s ease;
    will-change: transform;
}

.banner-scroll-item {
    flex: 0 0 auto;
    position: relative;
}

.banner-scroll-nav {
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 999px;
    background: #f7d7bc;
    color: #fff;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    transition: opacity 0.2s ease, background 0.2s ease;
}

.banner-scroll-nav-icon {
    width: 14px;
    height: 14px;
    display: block;
}

.banner-scroll-nav:hover:not(:disabled) {
    background: #f0bf99;
}

.banner-scroll-nav:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}
</style>
